import { PartialState } from "../states";
import { SectionGrid } from "../layout/SectionLayout";
import { spotlightPointerMove } from "../../utils/spotlight";
import { useSectionLayout } from "../layout/sectionLayoutContext";
import { isShownGroup, sortGroups, type GroupSortKey } from "./groupSort";
import { GroupCard } from "./GroupCard";
import { MyGroupsHero } from "./MyGroupsHero";
import { isCurrentPeriod } from "../../hooks/useSelectedPeriod";
import type { GroupDailySpendSeriesResponse, MyGroupsDailySpendResponse, MyGroupsResponse } from "../../api/organisationReporting";

/**
 * The "My Groups" screen's main content: one card per group the person owns, sits above or belongs to, in the chosen
 * order and layout. Each card is about the group as a whole, for its owner: where it stands against its allowance, where
 * it is heading, and how its spend divides between tools (see {@link GroupCard}). With more than one group, a hero above
 * the cards sums them up and names the groups that need a look (see {@link MyGroupsHero}). Rendered only once the caller has a
 * loaded response - loading, empty, error and denied are the page's job.
 *
 * @param props.response - The loaded "my groups" response.
 * @param props.dailySpend - Each group's day-by-day spend series for the current period, or `null` when unavailable.
 */
export function MyGroupsSummary({
  response,
  dailySpend,
  sortKey = "hierarchy",
  hideEmptyGroups = false,
}: {
  response: MyGroupsResponse;
  dailySpend: MyGroupsDailySpendResponse | null;
  sortKey?: GroupSortKey;
  hideEmptyGroups?: boolean;
}): React.JSX.Element {
  const { mode } = useSectionLayout();
  const period = { periodStart: response.periodStart, periodEnd: response.periodEnd, generatedAt: response.generatedAt };
  const unsynced = response.vendorCompleteness.filter((vendor) => !vendor.hasEverSynced);
  const isCurrentMonth = isCurrentPeriod(response.periodStart);
  const seriesFor = (groupId: string) => dailySpend?.groups.find((series) => series.groupKind === groupId) ?? null;
  const sortedGroups = sortGroups(
    response.groups.filter((group) => isShownGroup(group, hideEmptyGroups)),
    sortKey,
    (groupId) => projectedTotalOf(seriesFor(groupId), isCurrentMonth),
  );

  return (
    <div onPointerMove={spotlightPointerMove}>
      {/* A never-synced vendor is a development-time fact, not something to show users: the notice is dev-only. */}
      {import.meta.env.DEV && unsynced.map((vendor) => (
        <PartialState key={vendor.vendorId} vendorName={vendor.vendorDisplayName} description={vendor.note ?? undefined} />
      ))}

      {/* With a single group the hero would only repeat its card. */}
      {response.groups.filter((group) => isShownGroup(group, false)).length > 1 && (
        <MyGroupsHero response={response} dailySpend={dailySpend} isCurrentMonth={isCurrentMonth} />
      )}

      <SectionGrid>
        {sortedGroups.map((group) => (
          <GroupCard
            key={group.groupId}
            group={group}
            series={seriesFor(group.groupId)}
            period={period}
            isCurrentMonth={isCurrentMonth}
            mode={mode}
          />
        ))}
      </SectionGrid>
    </div>
  );
}

/**
 * A group's projected spend for the period, from its daily series. Only the current month has one; a past
 * period has nothing left to project, so `null`.
 */
function projectedTotalOf(series: GroupDailySpendSeriesResponse | null, isCurrentMonth: boolean): number | null {
  return isCurrentMonth ? (series?.projection?.projectedTotal ?? null) : null;
}
